import React from "react";
import { Button, Col, Modal, Row, Spinner } from "react-bootstrap";
import { CSVLink } from "react-csv";
import { getCurrentDateTime } from "../../helpers/date";

interface ExportModalProps {
  showModal: boolean;
  setShowModal: any;
  interactionExportData: any[];
  noteTypes: any[];
  startDate: any;
  endDate: any;
  isLoading?: boolean;
}

const ExportModal = ({ showModal, setShowModal, interactionExportData, noteTypes, startDate, endDate, isLoading }: ExportModalProps) => {
  const interactionHeaders = [
    { label: "ID", key: "id" },
    { label: "Note Type", key: "name" },
    { label: "Created At", key: "created_at" },
    { label: "Creator First Name", key: "creator_firstname" },
    { label: "Creator Last Name", key: "creator_lastname" },
    { label: "Case ID", key: "case_id" },
    { label: "Case", key: "case_name" },
    { label: "Subject First Name", key: "subject_firstname" },
    { label: "Subject Last Name", key: "subject_lastname" },
  ];

  const sanitizeForCsv = (val: any) => {
    if (val === null || val === undefined) return "";
    if (typeof val === "string") {
      return val.replace(/\r\n/g, "\n").replace(/"/g, '""').trim();
    }
    if (typeof val === "number" || typeof val === "boolean") return val;
    if (val instanceof Date) return val.toISOString();

    try {
      return JSON.stringify(val).replace(/\r\n/g, "\n").replace(/"/g, '""').trim();
    } catch {
      return String(val).replace(/\r\n/g, "\n").replace(/"/g, '""').trim();
    }
  };

  const buildSummaryExport = () => {
    if (!interactionExportData) return [];
    const summary = interactionExportData.map((i: any) => {
      if (i?.created_at) {
        const utcDate = new Date(i?.created_at + " UTC");
        const pad = (n: any) => n.toString().padStart(2, "0");
        const year = utcDate.getFullYear();
        const month = pad(utcDate.getMonth() + 1);
        const day = pad(utcDate.getDate());
        const hours = pad(utcDate.getHours());
        const minutes = pad(utcDate.getMinutes());
        const seconds = pad(utcDate.getSeconds());
        const localTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        return { ...i, created_at: localTime };
      } else {
        return { ...i };
      }
    });

    return summary.map((item) => {
      const filtered: any = {};
      interactionHeaders.forEach((h) => (filtered[h.key] = sanitizeForCsv(item[h.key])));
      return filtered;
    });
  };

  const buildDetailsExport = () => {
    if (!interactionExportData) return { data: [], headers: [] };

    const detailed = interactionExportData.map((i: any) => {
      if (i?.created_at) {
        const utcDate = new Date(i?.created_at + " UTC");
        const pad = (n: any) => n.toString().padStart(2, "0");
        const year = utcDate.getFullYear();
        const month = pad(utcDate.getMonth() + 1);
        const day = pad(utcDate.getDate());
        const hours = pad(utcDate.getHours());
        const minutes = pad(utcDate.getMinutes());
        const seconds = pad(utcDate.getSeconds());
        const localTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        return { ...i, created_at: localTime };
      } else {
        return { ...i };
      }
    });

    const allKeys = Array.from(new Set(detailed.flatMap(Object.keys)));
    const headers = allKeys.map((k) => ({ label: k, key: k }));

    const sanitizedData = detailed.map((row: any) => {
      const out: any = {};
      allKeys.forEach((k) => (out[k] = sanitizeForCsv(row[k])));
      return out;
    });

    return { data: sanitizedData, headers };
  };

  const buildNoteTypeExport = (noteType: string) => {
    if (!interactionExportData) return { data: [], headers: [] };

    const byType = interactionExportData.map((i: any) => {
      if (i?.created_at) {
        const utcDate = new Date(i?.created_at + " UTC");
        const pad = (n: any) => n.toString().padStart(2, "0");
        const year = utcDate.getFullYear();
        const month = pad(utcDate.getMonth() + 1);
        const day = pad(utcDate.getDate());
        const hours = pad(utcDate.getHours());
        const minutes = pad(utcDate.getMinutes());
        const seconds = pad(utcDate.getSeconds());
        const localTime = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        return { ...i, created_at: localTime };
      } else {
        return { ...i };
      }
    });

    const filteredNotes = byType.filter((n) => n.name === noteType);
    const allKeys = Array.from(new Set(filteredNotes.flatMap(Object.keys)));
    const headers = allKeys.map((k) => ({ label: k, key: k }));

    const sanitizedData = filteredNotes.map((row: any) => {
      const out: any = {};
      allKeys.forEach((k) => (out[k] = sanitizeForCsv(row[k])));
      return out;
    });

    return { data: sanitizedData, headers };
  };

  const normalizeDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { timeZone: "UTC" });
  };

  const formattedDateRange = `${normalizeDate(startDate)} - ${normalizeDate(endDate)}`;

  const handleClose = () => {
    setShowModal(false);
  };

  return (
    <Modal show={showModal} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title>Data Export Options</Modal.Title>
          <p className="m-0 text-muted">({formattedDateRange})</p>
        </div>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#F6893D" }} />
            <p className="mt-3 text-muted">Loading interaction data...</p>
          </div>
        ) : (
          <>
            <p className="mb-3 text-muted">Choose what kind of export you'd like to generate.</p>
            <Row className="g-2">
              <Col xs={12} md={6}>
                <CSVLink
                  data={buildSummaryExport()}
                  headers={interactionHeaders}
                  filename={`interactions_summary_${getCurrentDateTime(true)}.csv`}
                  style={{ display: "block" }}
                >
                  <Button className="button app-primary-color w-100 text-center py-2">Summary (no note values)</Button>
                </CSVLink>
              </Col>
              <Col xs={12} md={6}>
                <CSVLink
                  data={buildDetailsExport().data}
                  headers={buildDetailsExport().headers}
                  filename={`interactions_details_${getCurrentDateTime(true)}.csv`}
                  style={{ display: "block" }}
                >
                  <Button className="button app-primary-color w-100 text-center py-2">Detailed (with note values)</Button>
                </CSVLink>
              </Col>
              <hr className="mt-4" />
              <p className="text muted mb-1">All Note Types (with note vales)</p>
              {noteTypes.length > 0 ? (
                noteTypes.map((type: string) => {
                  const { data, headers } = buildNoteTypeExport(type);
                  const normalizedType = type.toLowerCase().replace(/[\s-]+/g, "_");
                  return (
                    <Col xs={12} md={6} key={type}>
                      <CSVLink
                        data={data}
                        headers={headers}
                        filename={`interactions_${normalizedType}_${getCurrentDateTime(true)}.csv`}
                        style={{ display: "block" }}
                      >
                        <Button className="button app-primary-color w-100 text-center py-2">{type}</Button>
                      </CSVLink>
                    </Col>
                  );
                })
              ) : (
                <p className="mt-4 text-muted small">No note types available.</p>
              )}
            </Row>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ExportModal;
